import { Button, Dialog } from '../../components/ui';
import type { CopyEditState } from './copyEditConfirmation';

const WORDING_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'channel', label: 'Channel' },
  { key: 'type', label: 'Type' },
  ...Array.from({ length: 8 }, (_, i) => [
    { key: `e${i + 1}_subject`, label: `Message ${i + 1} subject` },
    { key: `e${i + 1}_body`, label: `Message ${i + 1} wording` },
  ]).flat(),
];

function previewWording(value: string | null | undefined): string {
  return value == null ? '(not recorded)' : value === '' ? '(empty)' : value;
}

/** No mutation lives in this component. Each dialog requires its own action. */
export function CopyEditConfirmationDialog({
  state,
  onCancel,
  onConfirmFirst,
  onConfirmSecond,
}: {
  state: CopyEditState;
  onCancel: () => void;
  onConfirmFirst: () => void;
  onConfirmSecond: () => void;
}) {
  if (state.phase !== 'first' && state.phase !== 'second') return null;
  const first = state.phase === 'first';
  return (
    <Dialog
      // Remount for the second action, including its own focus initialization.
      // Keeping focus on the first action would make two quick Enter presses
      // count as both confirmations without showing the second question.
      key={state.phase}
      open
      wide={first}
      title={first ? 'Review used message edit' : 'Confirm used message edit'}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={first ? onConfirmFirst : onConfirmSecond}>
            {first ? 'Continue to second confirmation' : 'Confirm and save this version'}
          </Button>
        </>
      }
    >
      {first ? (
        <>
          <p>This message has been used. Review the changes before continuing.</p>
          <p>Earlier DMs keep their recorded versions. This edit creates a new library version.</p>
          <dl>
            {WORDING_FIELDS.filter(
              ({ key }) => state.baseline.fields[key] !== state.proposal[key],
            ).map(({ key, label }) => (
              <div key={key}>
                <dt>{label}</dt>
                <dd>
                  <p>Current</p>
                  <p style={{ whiteSpace: 'pre-wrap' }}>
                    {previewWording(state.baseline.fields[key])}
                  </p>
                  <p>Proposed</p>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{previewWording(state.proposal[key])}</p>
                </dd>
              </div>
            ))}
          </dl>
        </>
      ) : (
        <>
          <p>Save the changes you just reviewed as a new version of this used message?</p>
          <p>Its library entry stays the same. Earlier DM selections do not change.</p>
          <p>If the entry changed while you were reviewing, the save will be refused.</p>
        </>
      )}
    </Dialog>
  );
}
